import { LightningElement, api } from 'lwc';

import { fetchAuthSession } from 'aws-amplify/auth';
import { get } from 'aws-amplify/api';

const STEP_KEYS = {
    address: {fields: ['street', 'country', 'state', 'city', 'zip_code', 'latitude', 'longitude'], committed: false},
    basicInfo: {fields: [ 'environment_type', 'pool_type', 'water_system', 'restroom_type'], committed: false},
    max_guests: {fields: ['max_guests'], committed: false},
    listing_price_bucket: {fields: ['listing_price_bucket'], committed: false},
    listing_photo: {fields: ['listing_photo'], committed: false},
    property_name: {fields: ['property_name'], committed: false},
    amenities: {fields: ['amenities'], committed: false},
    houseRules: {fields: [
        'infants_allowed',
        'infants_free',
        'alcohol_allowed',
        'smoking_allowed',
        'pets_allowed',
        'parties_allowed',
        'loud_music_allowed',
        'glass_containers_allowed',
        'outside_food_allowed',
        'outside_drinks_allowed',
        'photography_allowed',
        'commercial_filming_allowed'
    ], committed: false}
};

const STEP_LABELS_MAP = {
    address: 'Address',
    basicInfo: 'Basic Info',
    max_guests: 'Guest Amount',
    listing_price_bucket: 'Pricing',
    listing_photo: 'Upload Photos',
    property_name: 'Property Name',
    amenities: 'Amenities',
    houseRules: 'House Rules',
};

const NOT_REQUIRED_FIELDS = [
    'county'
];

const HOUSE_RULE_FIELDS = new Set(STEP_KEYS.houseRules.fields);

function updateStepTracker(stepTracker, baseFields) {
    return Object.fromEntries(
        Object.entries(stepTracker).map(([section, details]) => {
            const fields = details.fields;
            const newDetails = {
                fields,
                committed: fields.every(field =>
                    field === 'listing_photo' || field === 'listing_price_bucket' || field === 'amenities'
                        ? baseFields?.[field]?.length > 0
                        : HOUSE_RULE_FIELDS.has(field)
                            ? typeof baseFields?.[field] === 'boolean'
                        : NOT_REQUIRED_FIELDS.includes(field) || !!baseFields?.[field]
                )
            };
            return [section, newDetails];
        })
    );
}

export default class GetWetListingEdit extends LightningElement {
    _isLoading = false;
    _stepTracker = STEP_KEYS;
    _editedSections = {};
    _listing;
    _listingId;
    _loadedListingId;
    _isPreviewActive = true;
    activeStepKey;
    activeStepLabel;

    @api
    set listingId(val) {
        this._listingId = val;
        if (val) {
            Promise.resolve().then(() => this.loadListing());
        }
    }
    get listingId() {
        return this._listingId;
    }

    @api mobile;

    connectedCallback() {
        if (this._listingId) {
            this.loadListing();
        }
    }

    async loadListing() {
        if (!this._listingId) return;
        if (this._loadedListingId === this._listingId) return;

        this._isLoading = true;
        try {
            this._loadedListingId = this._listingId;
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            if (!idToken) {
                throw new Error('No idToken (not signed in)');
            }

            const op = get({
                apiName: 'api',
                path: `/cognito/listing-by-id?listingId=${this._listingId}`,
                options: {
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                    },
                },
            });

            const { body } = await op.response;
            const payload = await body.json();
            const listing = payload?.listing || payload;

            this._listing = listing;
            this._stepTracker = updateStepTracker(STEP_KEYS, listing || {});
        } catch (error) {
            this._loadedListingId = null;
            console.error('Error loading listing in getWetListingEdit.js', error);
            this.showToast('Failed to load listing', error?.message || error, 'error');
        } finally {
            this._isLoading = false;
        }
    }

    get statusLabel() {
        return this._listing?.status || 'pending approval';
    }

    get hasActiveStep() {
        return !!this.activeStepKey;
    }

    get progressRing() {
        const keysLength = Object.keys(this._stepTracker).length;
        const finished = Object.entries(this._stepTracker)
            .filter(([_, details]) => details.committed);

        let i = 0;
        const checksArr = Object.entries(this._stepTracker)
            .map(([section, details]) => {
                i += 1;
                return {
                    label: STEP_LABELS_MAP[section],
                    completed: details.committed,
                    edited: this._editedSections[section] === true,
                    keyId: `listing-edit-${section}`,
                    step: String(i),
                    value: section
                };
            });

        
        return {
            label: `${finished.length}/${keysLength}`,
            value: keysLength ? (finished.length / keysLength) * 100 : 0,
            checksArr
        };
    }

    handleChangeSection(event) {
        const stepKey = event.currentTarget?.dataset?.value;
        if (!stepKey) return;

        this.activeStepKey = stepKey;
        this.activeStepLabel = STEP_LABELS_MAP[stepKey];
        this._isPreviewActive = false;
    }

    handleBackToPreview() {
        this._isPreviewActive = true;
        this.activeStepKey = null;
        this.activeStepLabel = null;
    }

    async handleSaveChanges() {
        const onboardCmp = this.template.querySelector('x-get-wet-host-on-board');
        if (!onboardCmp || !this.activeStepKey) return;

        this._isLoading = true;
        try {
            const editedFields = onboardCmp.getEditedFieldsForStep(this.activeStepKey);
            const hasChanges = editedFields.length > 0;
            const editedSections = hasChanges ? [this.activeStepKey] : [];
            const updatedListing = await onboardCmp.saveListing({
                status: 'pending approval',
                editedFields,
                editedSections,
                suppressSuccessToast: true,
                suppressExit: true
            });

            if (updatedListing) {
                this._listing = {
                    ...this._listing,
                    ...updatedListing,
                    status: 'pending approval'
                };
                this._stepTracker = updateStepTracker(this._stepTracker, this._listing);
            }

            if (hasChanges) {
                this._editedSections = {
                    ...this._editedSections,
                    [this.activeStepKey]: true
                };
            }

            this.showToast(
                hasChanges ? 'Changes saved' : 'No changes detected',
                hasChanges
                    ? 'Your listing is pending approval.'
                    : 'This section already matched your saved listing.',
                hasChanges ? 'success' : 'info'
            );
            this.handleBackToPreview();
        } catch (error) {
            console.error('Error saving listing changes in getWetListingEdit.js', error);
            this.showToast('Failed to save changes', error?.message || error, 'error');
        } finally {
            this._isLoading = false;
        }
    }

    handleClose() {
        this.dispatchEvent(new CustomEvent('listingeditclose', {
            detail: { listingId: this._listingId },
            bubbles: true,
            composed: true
        }));
    }

    toastObj;
    showToast(title, message, variant, mode=null) {
        const modeP = mode
            ? mode
            : variant === 'success' || variant === 'info'
                ? 'dismissible'
                : 'sticky';

        this.toastObj = {
            variant,
            mode: modeP,
            message,
            header: {
                text: title
            }
        };
    }
}
